import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  CheckCircle2,
  CalendarDays,
  Clock3,
  Camera,
  Receipt,
  LoaderCircle,
  AlertCircle,
} from "lucide-react";
import { useApi } from "../utils/Api";
import studioImage from "../assets/studio-success.jpg";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

const formatDate = (value) => {
  if (!value) return "—";

  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return value;

  return `${day}/${month}/${year}`;
};

const formatTimeLabel = (value) => {
  if (!value) return "—";

  const [hourStr, minuteStr = "00"] = String(value).split(":");
  const hour = Number(hourStr);
  const minute = Number(minuteStr);

  if (Number.isNaN(hour) || Number.isNaN(minute)) return value;

  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
};

const getImageCount = (value) => {
  if (!value) return "";
  const match = String(value).match(/\d+/);
  return match ? match[0] : "";
};

const formatEditedImages = (value) => {
  const count = getImageCount(value);
  if (!count) return "";
  return `${count} edited image${Number(count) > 1 ? "s" : ""}`;
};

const ensureSessionText = (value) => {
  if (!value) return "";
  const original = String(value).trim();
  return original.toLowerCase().includes("photo session")
    ? original
    : `${original} photo session`;
};

export default function StudioSessionBookingSuccess() {
  const { apiFetch } = useApi();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [booking, setBooking] = useState(null);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setError("Missing payment session.");
      setLoading(false);
      return;
    }

    let intervalId;

    const loadBooking = async () => {
      try {
        const res = await apiFetch("verify_studio_payment", {
          session_id: sessionId,
        });

        if (!res?.success || !res?.data) {
          throw new Error(res?.message || "Unable to load booking details.");
        }

        setBooking(res.data);
        setError("");

        if (res.data.payment_status === "paid" && intervalId) {
          clearInterval(intervalId);
        }
      } catch (err) {
        setError(err.message || "Unable to load booking details.");
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
    intervalId = setInterval(loadBooking, 3000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [sessionId, apiFetch]);

  const paymentText = useMemo(() => {
    if (!booking) return "";
    return money(booking.amount);
  }, [booking]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#1c1c1c] pt-28 px-4 py-10 sm:px-6">
        <div className="mx-auto max-w-[520px] rounded-2xl bg-white px-6 py-14 text-center shadow-sm sm:px-10">
          <div className="flex justify-center">
            <LoaderCircle className="h-12 w-12 animate-spin text-black" />
          </div>
          <p className="mt-5 text-base text-[#333]">
            Loading booking details...
          </p>
        </div>
      </main>
    );
  }

  if (error || !booking) {
    return (
      <main className="min-h-screen bg-[#1c1c1c] pt-28 px-4 py-10 sm:px-6">
        <div className="mx-auto max-w-[520px] rounded-2xl bg-white px-6 py-14 text-center shadow-sm sm:px-10">
          <div className="flex justify-center">
            <AlertCircle className="h-12 w-12 text-red-500" />
          </div>

          <h1 className="mt-5 text-2xl font-semibold text-[#111]">
            Unable to load booking
          </h1>

          <p className="mt-3 text-base text-[#444]">
            {error ||
              "Something went wrong while loading your booking details."}
          </p>

          <Link
            to="/bookings"
            className="mt-8 inline-flex rounded-xl bg-pink-500 px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
          >
            Back to bookings
          </Link>
        </div>
      </main>
    );
  }

  if (booking.payment_status !== "paid") {
    return (
      <main className="min-h-screen bg-[#1c1c1c] pt-28 px-4 py-10 sm:px-6">
        <div className="mx-auto max-w-[520px] rounded-2xl bg-white px-6 py-14 text-center shadow-sm sm:px-10">
          <div className="flex justify-center">
            <LoaderCircle className="h-12 w-12 animate-spin text-black" />
          </div>

          <h1 className="mt-6 text-2xl font-semibold text-[#111]">
            Processing your payment...
          </h1>

          <p className="mt-3 text-base text-[#444]">
            Please wait while we confirm your studio session booking.
          </p>

          {booking.booking_ref && (
            <p className="mt-4 text-sm text-[#666]">
              Reference:{" "}
              <span className="font-medium text-[#222]">
                {booking.booking_ref}
              </span>
            </p>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#1c1c1c] pt-28 px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-[520px] overflow-hidden rounded-2xl bg-white shadow-sm">
        <div className="px-6 pb-8 pt-10 text-center sm:px-10">
          <div className="flex justify-center">
            <CheckCircle2 size={62} strokeWidth={1.8} className="text-black" />
          </div>

          <h1 className="mt-5 text-3xl font-semibold tracking-tight text-[#1c1c1c]">
            Thanks for booking!
          </h1>

          <p className="mx-auto mt-4 max-w-[360px] text-lg leading-9 text-[#2b2b2b]">
            Your studio session booking is confirmed.
          </p>

          {booking.booking_ref && (
            <p className="mt-3 text-sm text-[#666]">
              Reference:{" "}
              <span className="font-medium text-[#222]">
                {booking.booking_ref}
              </span>
            </p>
          )}
        </div>

        <div className="h-[150px] w-full sm:h-[170px]">
          <img
            src={studioImage}
            alt="Studio session"
            className="h-full w-full object-cover"
          />
        </div>

        <div className="px-6 py-8 sm:px-10">
          <h2 className="text-[21px] font-medium text-[#222222]">
            Session Details
          </h2>

          <div className="mt-7 space-y-6">
            <div className="flex items-center gap-4">
              <p className="text-[17px] font-bold leading-7 text-[#222222]">
                {booking.package_name || "Studio session"}
              </p>
            </div>

            {booking.session_duration && (
              <div className="flex items-center gap-4">
                <Camera size={24} strokeWidth={1.9} className="text-black" />
                <p className="text-[17px] leading-7 text-[#222222]">
                  {ensureSessionText(booking.session_duration)}
                </p>
              </div>
            )}

            {booking.edited_images && (
              <div className="flex items-center gap-4">
                <Camera size={24} strokeWidth={1.9} className="text-black" />
                <p className="text-[17px] leading-7 text-[#222222]">
                  {formatEditedImages(booking.edited_images)}
                </p>
              </div>
            )}

            {booking.extra && (
              <div className="flex items-center gap-4">
                <Camera size={24} strokeWidth={1.9} className="text-black" />
                <p className="text-[17px] leading-7 text-[#222222]">
                  {booking.extra}
                </p>
              </div>
            )}

            <div className="flex items-center gap-4">
              <CalendarDays
                size={24}
                strokeWidth={1.9}
                className="text-black"
              />
              <p className="text-[17px] leading-7 text-[#222222]">
                {formatDate(booking.booking_date)}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <Clock3 size={24} strokeWidth={1.9} className="text-black" />
              <p className="text-[17px] leading-7 text-[#222222]">
                {booking.start_time && booking.end_time
                  ? `${formatTimeLabel(booking.start_time)} - ${formatTimeLabel(booking.end_time)}`
                  : formatTimeLabel(booking.booking_time)}
              </p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="text-[21px] font-medium text-[#222222]">Payment</h2>

            <div className="mt-5 flex items-start gap-4">
              <Receipt
                size={24}
                strokeWidth={1.9}
                className="mt-1 text-black"
              />
              <p className="text-[17px] leading-7 text-[#222222]">
                <span className="font-medium">{paymentText}</span> was
                successfully paid.
              </p>
            </div>

            {booking.payment_status && (
              <p className="mt-4 text-sm text-[#666]">
                Status:{" "}
                <span className="font-medium capitalize text-[#222]">
                  {booking.payment_status}
                </span>
              </p>
            )}
          </div>

          <div className="mt-10">
            <Link
              to="/book"
              className="inline-flex rounded-xl bg-pink-500 px-5 py-3 text-sm font-semibold text-white transition hover:opacity-90"
            >
              Book another session
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
